<template>
  <div>
    <member-content-title :title="$t('all_fun_coin')" />

    <div class="wd-border-0 wd-border-b wd-border-solid wd-border-gray40 wd--mx-5 wd-mb-5 wd-px-5 wd-pb-6 sm:wd-mx-0 sm:wd-mb-4 sm:wd-px-0 sm:wd-pb-10">
      <div class="wd-flex wd-justify-center wd-items-center wd-gap-2 wd-mt-4">
        <span class="wd-text-otempmax wd-font-medium">{{ formattedFunCoinsText }}</span>
        <funnow-icon active-class="wd-h-10 wd-w-10" image-path="fun_coins.svg" />
      </div>
      <p v-if="bonusValidDateText" class="wd-text-omd wd-mt-4 !wd-mb-0 wd-text-center">{{ bonusValidDateText }}</p>
    </div>

    <p class="wd-text-otemplg wd-font-medium wd-py-4 !wd-mb-0">{{ $t('funcoin_how_to_get_title') }}</p>

    <div v-for="block in contentBlocks" :key="block.type" class="wd-flex wd-gap-4 wd-py-5 sm:wd-px-10 sm:wd--mx-5">
      <div class="wd-flex-shrink-0 wd-p-1">
        <funnow-icon :image-path="block.imagePath" width="64" height="64" raw />
      </div>
      <div class="wd-flex-1">
        <h3 class="wd-text-omd wd-font-medium wd-mb-2">{{ block.title }}</h3>
        <p class="wd-text-otempsm wd-whitespace-pre-line !wd-mb-0">
          {{ block.content }}
        </p>
      </div>
    </div>

    <a v-if="faqLink" :href="faqLink" target="_blank" class="wd-flex wd-items-center wd-justify-between wd--mr-3 wd-py-2">
      <span class="wd-text-omd wd-text-black50">{{ $t('all_faq') }}</span>
      <funnow-icon image-path="chevron-right.svg" active-class="wd-text-gray60 wd-h-10 wd-w-10 wd-p-2" />
    </a>
  </div>
</template>

<script setup lang="ts">
import { useIntlNumber } from '@/composition/useIntl'
import { useFunCoin } from '@/composition/useMember'
import { useFunCoinsInfo } from '@/composition/useFunCoins'
import { useMemberPageAuth } from '@/composition/useMemberPageAuth'

import MemberContentTitle from '@/components/member/MemberContentTitle.vue'
import FunnowIcon from '@/components/icons/funnow-icon.vue'

import { FAQ_LINK, FAQ_SECTION_IDS } from '@/constants/links'
import type { FmemberMemberInfo, FunnowRegionRegionInfoRes } from '@/types/api'
import { RegionIdMap } from '@/view-models/region'
import { useRegionStore } from '@/store-pinia/region'

definePageMeta({
  layout: 'member',
})

const regionStore = useRegionStore()
const { regionDetectInfo } = storeToRefs(regionStore)

const { $store: store, $dayjs } = useNuxtApp()
const { getLocaleNumber } = useIntlNumber()
const { funCoinsImages } = useFunCoin()
const { locale, t } = useI18n()
const { state: funCoinsInfo, execute: fetchFunCoinsInfo } = useFunCoinsInfo()

useMemberPageAuth()

const user = computed<FmemberMemberInfo | null>(() => store.getters['authentication/getUser'])
const region = computed<FunnowRegionRegionInfoRes>(() => store.getters['region/region'])
const faqLink = computed(() => {
  const baseUrl = FAQ_LINK[locale.value]

  return baseUrl ? `${baseUrl}/sections/${FAQ_SECTION_IDS.funCoin}` : ''
})
const contentBlocks = computed(() => [
  {
    type: 'review',
    imagePath: funCoinsImages.value[0],
    title: t('funcoin_gain_by_review_title'),
    content: t('funcoin_gain_by_review_content'),
  },
  {
    type: 'rule',
    imagePath: funCoinsImages.value[1],
    title: t('funcoin_rule_title'),
    content: t('funcoin_rule_content'),
  },
])
const formattedFunCoinsText = computed(() => getLocaleNumber(user.value?.bonus || 0))
const bonusAmount = computed(() => funCoinsInfo.value?.bonus_amount || 0)
const bonusValidDateText = computed(() => {
  return user.value?.bonus_valid_date && bonusAmount.value > 0
    ? t('funcoin_amount_deadline', {
        currency: funCoinsInfo.value?.symbol_code,
        amount: getLocaleNumber(bonusAmount.value),
        deadline: $dayjs(user.value.bonus_valid_date).format('YYYY/MM/DD'),
      })
    : ''
})

watch(
  region,
  newRegion => {
    fetchFunCoinsInfo(newRegion?.currency_id)
  },
  { immediate: true }
)

onMounted(() => {
  if (!region.value?.id) {
    const regionId = regionDetectInfo.value?.id ?? RegionIdMap.Taipei

    store.dispatch('region/getRegion', regionId)
  }
})
</script>
